import DigiobcanApi, {
  type AdminReportDetail,
  type MediaCensor,
} from '../../../../api/DigiobcanApi';
import { useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import BrushIcon from '@mui/icons-material/Brush';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Media = AdminReportDetail['media'][number];

type Props = {
  reportId: string;
  media: Media;
  onClose: () => void;
  /** Po uložení aj po odstránení cenzúry - detail si prílohy načíta znova. */
  onSaved: () => Promise<unknown>;
};

/**
 * Koľko buniek mriežky pripadne na dlhšiu stranu fotky. Bunky sú približne
 * štvorcové, takže na fotke z mobilu (1920 px) má štvorček ~40 px - dosť na
 * to, aby sa z mozaiky tvár ani ŠPZ nedala prečítať, a dosť jemne, aby sa
 * dalo zakryť len to, čo treba.
 */
const CELLS_ON_LONG_SIDE = 48;

/** Polomer štetca v bunkách - malý zakryje jednu bunku, veľký kus tváre. */
const BRUSH_SIZES = [
  { radius: 0.5, label: __('Malý') },
  { radius: 1.5, label: __('Stredný') },
  { radius: 3, label: __('Veľký') },
] as const;

type Grid = { cols: number; rows: number };

const gridFor = (width: number, height: number, censor?: MediaCensor): Grid => {
  // Existujúca maska drží svoju mriežku - iná by jej bunky posunula.
  if (censor) return { cols: censor.cols, rows: censor.rows };
  const cell = Math.ceil(Math.max(width, height) / CELLS_ON_LONG_SIDE);
  return {
    cols: Math.min(200, Math.max(1, Math.ceil(width / cell))),
    rows: Math.min(200, Math.max(1, Math.ceil(height / cell))),
  };
};

/**
 * Hranica bunky v pixeloch. API zväčšuje mriežku na rozmer fotky bez
 * vyhladenia, takže bunka `i` zaberá `[i * size / count, (i + 1) * …)` - tou
 * istou aritmetikou sa tu kreslí náhľad, aby sedel s tým, čo sa uloží.
 */
const edge = (index: number, count: number, size: number) =>
  Math.round((index * size) / count);

/**
 * Editor cenzúry cez celú obrazovku.
 *
 * Kreslí sa **nad originálom** (`adminGetReportMediaOriginal`), nie nad
 * verejnou kópiou: tá je už rozmazaná a guma by pod ňou nemala čo odkryť.
 * Mozaiku v náhľade skladá prehliadač sám - fotku zmenší na mriežku (každý
 * pixel je priemer bunky) a zakryté bunky vyplní tou farbou. Hotový obrázok
 * potom skladá API z masky rovnakým spôsobom, takže sa sem neposiela nič
 * iné než zoznam buniek.
 */
const CensorEditor: React.FC<Props> = ({
  reportId,
  media,
  onClose,
  onSaved,
}) => {
  const { logger } = useLogger();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [image, setImage] = React.useState<HTMLImageElement>();
  const [grid, setGrid] = React.useState<Grid>();
  const averagesRef = React.useRef<Uint8ClampedArray>(undefined);
  const [cells, setCells] = React.useState<Set<number>>(
    () => new Set(media.censor?.cells ?? []),
  );
  const [history, setHistory] = React.useState<Set<number>[]>([]);
  const [mode, setMode] = React.useState<'brush' | 'eraser'>('brush');
  const [brush, setBrush] = React.useState(1);
  const [cursor, setCursor] = React.useState<{ x: number; y: number }>();
  const drawingRef = React.useRef(false);

  // Originál sa ťahá s tokenom - verejne podávaný nie je.
  React.useEffect(() => {
    let cancelled = false;
    let url: string | undefined;

    (async () => {
      try {
        const response = (await DigiobcanApi.adminGetReportMediaOriginal(
          reportId,
          media.media_id!,
        )) as unknown as Response;
        url = URL.createObjectURL(await response.blob());
        const img = new window.Image();
        img.src = url;
        await img.decode();
        if (cancelled) return;

        const nextGrid = gridFor(
          img.naturalWidth,
          img.naturalHeight,
          media.censor,
        );
        const small = document.createElement('canvas');
        small.width = nextGrid.cols;
        small.height = nextGrid.rows;
        const context = small.getContext('2d')!;
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(img, 0, 0, nextGrid.cols, nextGrid.rows);
        averagesRef.current = context.getImageData(
          0,
          0,
          nextGrid.cols,
          nextGrid.rows,
        ).data;

        setGrid(nextGrid);
        setImage(img);
      } catch (error) {
        if (cancelled) return;
        logger.error('Failed to load media original', {
          action: 'loadMediaOriginal',
          error,
          data: { reportId, mediaId: media.media_id },
        });
        toastError(__('Fotku sa nepodarilo načítať.'));
        onClose();
      }
    })();

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // Načítava sa raz pri otvorení; ďalšie zmeny prílohy editor nesleduje.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prekreslenie: originál, mozaika zakrytých buniek a obrys štetca.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    const averages = averagesRef.current;
    if (!canvas || !image || !grid || !averages) return;

    const width = image.naturalWidth;
    const height = image.naturalHeight;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d')!;
    context.drawImage(image, 0, 0);

    for (const cell of cells) {
      const x = cell % grid.cols;
      const y = Math.floor(cell / grid.cols);
      const i = cell * 4;
      context.fillStyle = `rgb(${averages[i]}, ${averages[i + 1]}, ${averages[i + 2]})`;
      const left = edge(x, grid.cols, width);
      const top = edge(y, grid.rows, height);
      context.fillRect(
        left,
        top,
        edge(x + 1, grid.cols, width) - left,
        edge(y + 1, grid.rows, height) - top,
      );
    }

    if (cursor) {
      const radius = (BRUSH_SIZES[brush].radius + 0.5) * (width / grid.cols);
      context.beginPath();
      context.arc(cursor.x, cursor.y, radius, 0, Math.PI * 2);
      context.lineWidth = Math.max(2, width / 400);
      context.strokeStyle = mode === 'brush' ? '#F73533' : '#FFFFFF';
      context.stroke();
    }
  }, [image, grid, cells, cursor, brush, mode]);

  /** Bod udalosti v pixeloch fotky - plátno je na obrazovke zmenšené. */
  const toImagePoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x:
        ((event.clientX - rect.left) / rect.width) * (image?.naturalWidth ?? 0),
      y:
        ((event.clientY - rect.top) / rect.height) *
        (image?.naturalHeight ?? 0),
    };
  };

  const paint = (point: { x: number; y: number }) => {
    if (!image || !grid) return;
    const cx = (point.x / image.naturalWidth) * grid.cols;
    const cy = (point.y / image.naturalHeight) * grid.rows;
    const radius = BRUSH_SIZES[brush].radius;

    setCells((previous) => {
      const next = new Set(previous);
      for (
        let y = Math.floor(cy - radius);
        y <= Math.floor(cy + radius);
        y += 1
      ) {
        for (
          let x = Math.floor(cx - radius);
          x <= Math.floor(cx + radius);
          x += 1
        ) {
          if (x < 0 || y < 0 || x >= grid.cols || y >= grid.rows) continue;
          // Vzdialenosť od stredu bunky - štetec je kruh, nie štvorec.
          if (Math.hypot(x + 0.5 - cx, y + 0.5 - cy) > radius + 0.5) continue;
          const cell = y * grid.cols + x;
          if (mode === 'brush') next.add(cell);
          else next.delete(cell);
        }
      }
      return next;
    });
  };

  const saveMutation = useM(
    async () => {
      if (cells.size) {
        await DigiobcanApi.adminCensorReportMedia(
          reportId,
          media.media_id!,
          undefined,
          { cols: grid!.cols, rows: grid!.rows, cells: [...cells] },
        );
      } else if (media.censor) {
        // Všetko zmazané gumou je to isté ako „Odstrániť cenzúru".
        await DigiobcanApi.adminUncensorReportMedia(reportId, media.media_id!);
      }
      return onSaved();
    },
    {
      onSuccess: () => {
        toastSuccess(
          cells.size
            ? __('Cenzúra bola uložená.')
            : __('Cenzúra bola odstránená.'),
        );
        onClose();
      },
      onError: (error) => {
        logger.error('Failed to censor report media', {
          action: 'censorReportMedia',
          error,
          data: { reportId, mediaId: media.media_id },
        });
        toastError(__('Cenzúru sa nepodarilo uložiť.'));
      },
    },
  );

  const busy = saveMutation.isPending;

  return (
    <Dialog
      open
      fullScreen
      onClose={busy ? undefined : onClose}
      // Téma dáva každému dialógu zaoblenie a rámik cez `.MuiDialog-paper`
      // na koreni - `sx` papiera to neprebije, preto sa prepisuje tu.
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 0,
          border: 'none',
          backgroundColor: 'grey.900',
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction='row'
          alignItems='center'
          flexWrap='wrap'
          gap={1.5}
          sx={{
            px: 2,
            py: 1.25,
            backgroundColor: 'background.paper',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mr: 1 }}>
            {__('Cenzúra fotky')}
          </Typography>

          <ToggleButtonGroup
            size='small'
            exclusive
            value={mode}
            onChange={(_, value) => value && setMode(value)}
          >
            <ToggleButton value='brush'>
              <BrushIcon fontSize='small' sx={{ mr: 0.75 }} />
              {__('Štetec')}
            </ToggleButton>
            <ToggleButton value='eraser'>
              <AutoFixNormalIcon fontSize='small' sx={{ mr: 0.75 }} />
              {__('Guma')}
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            size='small'
            exclusive
            value={brush}
            onChange={(_, value) => value !== null && setBrush(value)}
          >
            {BRUSH_SIZES.map((size, index) => (
              <ToggleButton key={size.label} value={index}>
                {size.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Button
            variant='text'
            size='small'
            disabled={!history.length || busy}
            onClick={() => {
              setCells(history[history.length - 1]);
              setHistory((previous) => previous.slice(0, -1));
            }}
            startIcon={<UndoIcon />}
          >
            {__('Späť')}
          </Button>

          <Button
            variant='text'
            size='small'
            disabled={!cells.size || busy}
            onClick={() => {
              setHistory((previous) => [...previous, cells]);
              setCells(new Set());
            }}
          >
            {__('Odstrániť cenzúru')}
          </Button>

          <Stack direction='row' gap={1} sx={{ ml: 'auto' }}>
            <Button variant='text' disabled={busy} onClick={onClose}>
              {__('Zrušiť')}
            </Button>
            <Button
              disabled={!grid || busy || (!cells.size && !media.censor)}
              loading={busy}
              onClick={() => saveMutation.mutate()}
            >
              {__('Uložiť')}
            </Button>
          </Stack>
        </Stack>

        {/* Plátno je absolútne v kontajneri s pevnou výškou: `maxHeight: 100%`
            sa z riadku s automatickou výškou neodvodí a fotka na výšku by
            pretiekla. Pri `inset` a `margin: auto` si plátno drží pomer strán
            a sedí v strede. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {image ? (
            <Box
              component='canvas'
              ref={canvasRef}
              onPointerDown={(event: React.PointerEvent<HTMLCanvasElement>) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                drawingRef.current = true;
                // Jeden ťah = jeden krok späť.
                setHistory((previous) => [...previous, cells]);
                paint(toImagePoint(event));
              }}
              onPointerMove={(event: React.PointerEvent<HTMLCanvasElement>) => {
                const point = toImagePoint(event);
                setCursor(point);
                if (drawingRef.current) paint(point);
              }}
              onPointerUp={() => {
                drawingRef.current = false;
              }}
              onPointerCancel={() => {
                drawingRef.current = false;
              }}
              onPointerLeave={() => setCursor(undefined)}
              sx={{
                position: 'absolute',
                inset: 16,
                margin: 'auto',
                maxWidth: 'calc(100% - 32px)',
                maxHeight: 'calc(100% - 32px)',
                // Bez toho by ťah prstom na mobile posúval stránku.
                touchAction: 'none',
                cursor: 'none',
                boxShadow: 6,
              }}
            />
          ) : (
            <CircularProgress sx={{ color: 'common.white' }} />
          )}
        </Box>
      </Stack>
    </Dialog>
  );
};

export default CensorEditor;
